// AI-CONTEXT: See docs/features/subscriptions-overview-page.md
import { useState } from 'react'
import firebase from '../../firebase/compat'
import TableCell from '../common/table/TableCell.jsx'
import SubscriptionNoteCell from './SubscriptionNoteCell'
import SubscriptionStatusCell from './SubscriptionStatusCell'
import formatEuro from '../../helpers/formatEuro'
import { centsToInput, inputToPayload } from '../../helpers/subscriptionHelpers/invoiceAmountInput'
import parentLabel from '../../helpers/subscriptionHelpers/parentLabel'

const CENTS_PER_EURO = 100

/**
 * Wat er in Subscriptions.billingType staat, in gewone taal. Alleen deze twee
 * komen voor (250× invoice, 13× stripe, gemeten 24-09-2026); een waarde die we
 * niet kennen tonen we onveranderd in plaats van als streepje — dan is te zien
 * dat er iets bij is gekomen.
 */
const BILLING_TYPE_LABELS = {
    invoice: 'Factuur',
    stripe: 'Stripe',
}

const MODULE_LABELS = {
    impactMeten: 'Impact meten',
    personas: 'Deelnemers',
    housevisits: 'FIXtool',
    portfolio: 'Portfolio',
}

const euro = (cents) => (Number.isFinite(cents) ? formatEuro(cents / CENTS_PER_EURO) : '—')

/**
 * Wat de server na een opslag over de rij zegt. `status` is de stand die in de
 * kolom hoort te staan (met de handmatig gezette stand er al in verwerkt),
 * `paymentStatus` wat de betaalgegevens zeggen en `editable` of er hier een
 * bedrag ingevuld mag worden. De rij leidt er zelf niets van af, want dan zou
 * er een tweede waarheid naast de serverhelpers ontstaan.
 *
 * `monthlyCents` is de uitzondering: wint Stripe, dan kent de server het
 * maandbedrag niet (dat komt uit Stripe zelf, via het overzicht). Wat er stond
 * blijft dan staan; een null zou het bedrag stil laten verdwijnen.
 *
 * @param {object} row - de rij zoals hij nu is
 * @param {object|undefined} data - result.data van de callable
 * @returns {{status: string, paymentStatus: string, editable: boolean, monthlyCents: number|null}}
 */
const serverStand = (row, data) => {
    const paymentStatus = data?.paymentStatus || row.paymentStatus
    return {
        status: data?.status || row.status,
        paymentStatus,
        editable: data?.editable ?? row.editable,
        monthlyCents: paymentStatus === 'stripe' ? row.monthlyCents : (data?.monthlyCents ?? null),
    }
}

const dateLabel = (iso) => {
    if (!iso) return null
    const [jaar, maand, dag] = iso.split('-')
    return `${Number(dag)}-${Number(maand)}-${jaar}`
}

/**
 * Eén regel in het abonnementenoverzicht, met de factuurafspraak en de
 * einddatum direct aanpasbaar.
 *
 * Twee losse opslagknoppen, geen gedeelde: bedrag en begindatum gaan naar
 * setSubscriptionInvoice, de einddatum naar setSubscriptionEndDate. Dat zijn
 * twee bestaande schrijvers en die blijven gescheiden — één knop zou hier een
 * derde schrijver of een volgorde-afhankelijke dubbelaanroep worden.
 *
 * De invoervelden leven in deze component en worden NIET opnieuw gevuld als de
 * pagina nieuwe props doorgeeft. De pagina vervangt na elke opslag één rij in
 * haar lijst; zou dat de velden terugzetten, dan verdwijnt het bedrag dat je in
 * een andere rij aan het typen bent. Bij ~94 in te vullen rijen vul je er
 * meerdere tegelijk.
 * AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
 *
 * Let op: die bescherming gaat over binnenkomende PROPS. Tegen unmount helpt ze
 * niet — verdwijnt de rij uit de lijst, dan is alles hieronder weg. Daarom
 * verbergt de pagina niet-passende rijen bij het zoeken in plaats van ze eruit
 * te filteren; lees dat mee voor je hier iets aan de lijst verandert.
 * AI-CONTEXT: See docs/pitfalls.md#ui-filtering-a-list-unmounts-unsaved-input
 *
 * @param {object} props
 * @param {object} props.row - rij uit getSubscriptionsOverview
 * @param {(row: object) => void} props.onSaved - krijgt de bijgewerkte rij na een geslaagde opslag
 */
const SubscriptionRow = ({ row, onSaved }) => {
    // Beginwaarde uit de props, daarna van de gebruiker. Bewust géén useEffect
    // die meeloopt met `row` — zie de toelichting hierboven.
    const [amountInput, setAmountInput] = useState(centsToInput(row.invoiceAmountCents))
    const [startInput, setStartInput] = useState(row.invoicePeriodStart || '')
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState(/** @type {{type: 'ok'|'error', text: string} | null} */ (null))
    const [endInput, setEndInput] = useState(row.endDate || '')
    const [savingEnd, setSavingEnd] = useState(false)
    const [endMessage, setEndMessage] = useState(/** @type {{type: 'ok'|'error', text: string} | null} */ (null))

    const handleSave = async () => {
        setMessage(null)
        setSaving(true)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const setSubscriptionInvoice = firebase.app().functions('europe-west1')
                .httpsCallable('setSubscriptionInvoice')
            const result = await setSubscriptionInvoice({
                compagnyId: row.compagnyId,
                ...inputToPayload(amountInput, startInput),
            })
            setMessage({ type: 'ok', text: 'Opgeslagen' })
            // De status komt van de server, uit dezelfde helper die de tegel
            // voedt — de pagina leidt hem niet zelf af.
            onSaved({
                ...row,
                invoiceAmountCents: result.data?.amountCents ?? null,
                invoicePeriodStart: inputToPayload(amountInput, startInput).periodStart || null,
                ...serverStand(row, result.data),
            })
        } catch (err) {
            console.error('setSubscriptionInvoice failed:', err)
            setMessage({ type: 'error', text: err.message || 'Kon het bedrag niet opslaan.' })
        } finally {
            setSaving(false)
        }
    }

    const handleSaveEndDate = async () => {
        setEndMessage(null)
        setSavingEnd(true)
        try {
            // v2-functie in europe-west1 — region expliciet, anders belt de SDK
            // us-central1 en bestaat de functie daar niet.
            const setSubscriptionEndDate = firebase.app().functions('europe-west1')
                .httpsCallable('setSubscriptionEndDate')
            const result = await setSubscriptionEndDate({
                compagnyId: row.compagnyId,
                // Leeg veld = einddatum wissen; de server kent dat contract.
                endDate: endInput || '',
            })
            setEndMessage({ type: 'ok', text: 'Opgeslagen' })
            // Ook hier komt de status van de server, uit dezelfde helper die de
            // tegel voedt: na het verlengen mag de rij niet nog "verlopen" heten.
            onSaved({
                ...row,
                endDate: endInput || null,
                ...serverStand(row, result.data),
            })
        } catch (err) {
            console.error('setSubscriptionEndDate failed:', err)
            setEndMessage({ type: 'error', text: err.message || 'Kon de einddatum niet opslaan.' })
        } finally {
            setSavingEnd(false)
        }
    }

    const periodEnd = dateLabel(row.endDate)

    return (
        <>
            <TableCell>
                {row.name || '—'}
                {/* Twee organisaties heten allebei "SpijkerBikes". Alleen dáár
                    de koepel erbij: 73 van de 99 hebben er een, dus die regel
                    overal zetten maakt de tabel hoger zonder iets op te lossen —
                    en hier is het verschijnen van de regel zelf het signaal dat
                    je moet opletten. */}
                {row.nameIsShared && (
                    <span className='subscriptions-parent'>{parentLabel(row)}</span>
                )}
            </TableCell>
            {/* De volgorde hier moet gelijk lopen met COLUMNS in
                SubscriptionsOverview.jsx — zie de kolomtest daar. */}
            <SubscriptionStatusCell row={row} onSaved={onSaved} />
            <SubscriptionNoteCell row={row} onSaved={onSaved} />
            <TableCell>
                {row.modules.length
                    ? row.modules.map((slug) => MODULE_LABELS[slug] || slug).join(', ')
                    : '—'}
            </TableCell>
            <TableCell>
                {row.editable ? (
                    <div className='subscriptions-edit'>
                        <input
                            type='text'
                            inputMode='decimal'
                            aria-label='Bedrag'
                            placeholder='1200,00'
                            className='subscriptions-edit-amount'
                            value={amountInput}
                            onChange={(e) => setAmountInput(e.target.value)}
                        />
                        <input
                            type='date'
                            aria-label='Periode begint'
                            className='subscriptions-edit-date'
                            value={startInput}
                            onChange={(e) => setStartInput(e.target.value)}
                        />
                        {/* aria-label omdat de rij twee opslagknoppen heeft;
                            'Opslaan' alleen zou niet zeggen wélke. */}
                        <button
                            type='button'
                            aria-label='Bedrag opslaan'
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '…' : 'Opslaan'}
                        </button>
                        {message && (
                            <span className={message.type === 'error'
                                ? 'subscriptions-edit-message subscriptions-edit-message--error'
                                : 'subscriptions-edit-message'}>
                                {message.text}
                            </span>
                        )}
                    </div>
                ) : (
                    // Deze kolom gaat over de handmatige factuurafspraak. Bij een
                    // Stripe-klant is die er niet — het maandbedrag staat in de
                    // laatste kolom, en dat hier herhalen zou hetzelfde getal
                    // twee keer op één rij zetten.
                    euro(row.invoiceAmountCents)
                )}
            </TableCell>

            <TableCell>
                {row.editable ? (
                    <div className='subscriptions-edit'>
                        <input
                            type='date'
                            aria-label='Loopt tot'
                            className='subscriptions-edit-date'
                            value={endInput}
                            onChange={(e) => setEndInput(e.target.value)}
                        />
                        <button
                            type='button'
                            aria-label='Einddatum opslaan'
                            onClick={handleSaveEndDate}
                            disabled={savingEnd}
                        >
                            {savingEnd ? '…' : 'Opslaan'}
                        </button>
                        {endMessage && (
                            <span className={endMessage.type === 'error'
                                ? 'subscriptions-edit-message subscriptions-edit-message--error'
                                : 'subscriptions-edit-message'}>
                                {endMessage.text}
                            </span>
                        )}
                    </div>
                ) : (
                    periodEnd ? `t/m ${periodEnd}` : '—'
                )}
            </TableCell>

            <TableCell>
                {BILLING_TYPE_LABELS[row.billingType] || row.billingType || '—'}
            </TableCell>
            {/* Het maandbedrag als laatste: het is de uitkomst van alles wat
                links ervan staat — de factuurafspraak of het Stripe-bedrag. */}
            <TableCell>{euro(row.monthlyCents)}</TableCell>
        </>
    )
}

export default SubscriptionRow
